/**
 * Path parsing utilities for extracting information from URL pathnames
 */

export interface PathInfo {
    searchId: string | null;
    isResultsPage: boolean;
}

/**
 * Helper function to extract search ID from path segments
 * @param segments - Array of path segments
 * @returns Search ID if found, null otherwise
 */
const extractSearchIdFromSegments = (segments: string[]): string | null => {
    // Define route patterns and their ID positions
    const routePatterns = [
        { segment: 'results', idPosition: 1 },
        { segment: 'search-request', idPosition: 1 }
    ];
    
    for (const pattern of routePatterns) {
        const segmentIndex = segments.indexOf(pattern.segment);
        if (segmentIndex !== -1) {
            const idIndex = segmentIndex + pattern.idPosition;
            if (idIndex < segments.length) {
                return segments[idIndex];
            }
        }
    }
    
    return null;
};

/**
 * Extracts search-related information from a URL pathname
 * @param pathname - The pathname to parse
 * @returns PathInfo object containing searchId and page type information
 */
export const extractSearchPathInfo = (pathname: string): PathInfo => {
    if (!pathname) {
        return { searchId: null, isResultsPage: false };
    }

    // Split path into segments and filter out empty strings
    const segments = pathname.split('/').filter(Boolean);
    
    // Check if this is a results page
    const isResultsPage = segments.includes('results');
    
    // Extract search ID using more declarative approach
    const searchId = extractSearchIdFromSegments(segments);
    
    return {
        searchId,
        isResultsPage
    };
};